import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import LanguageSwitcher from './LanguageSwitcher';
import { useTranslation } from 'react-i18next';

interface NavBarProps {
  transparent?: boolean;
}

const NavBar: React.FC<NavBarProps> = ({ transparent = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const { t } = useTranslation();
  
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 计算背景透明度
  const getBackgroundOpacity = () => {
    if (transparent) {
      // 当滚动位置小于 50px 时完全透明，大于 200px 时完全不透明
      const opacity = Math.min(Math.max((scrollY - 50) / 150, 0), 1);
      return opacity;
    }
    return 1;
  };

  // 计算文字颜色
  const getTextColor = () => {
    if (transparent) {
      const opacity = getBackgroundOpacity();
      return opacity > 0.5 ? 'text-white' : 'text-black';
    }
    return 'text-black';
  };

  return (
    <nav 
      className={cn(
        'fixed w-full z-50 px-6 md:px-12 py-6 transition-all duration-300',
        transparent ? 'bg-transparent' : 'bg-background shadow-sm'
      )}
      style={{
        background: transparent 
          ? `linear-gradient(to bottom, rgba(17, 17, 17, ${getBackgroundOpacity() * 0.6}), rgba(17, 17, 17, 0))`
          : undefined
      }}
    >
      <div className="flex items-center justify-between">
        <Link to="/" className={cn("text-2xl font-lora font-semibold tracking-wider transition-colors duration-300", getTextColor())}>
          Swain Photography
        </Link>
        
        <div className="hidden md:flex items-center space-x-8">
          <Link 
            to="/" 
            className={cn(
              "text-sm uppercase tracking-wider transition-colors duration-300",
              getTextColor(),
              "hover:text-photo-accent"
            )}
          >
            {t('home.footer.portfolio')}
          </Link>
          <Link 
            to="/gallery" 
            className={cn(
              "text-sm uppercase tracking-wider transition-colors duration-300",
              getTextColor(),
              "hover:text-photo-accent"
            )}
          >
            {t('home.footer.gallery')}
          </Link>
          <div className={cn("transition-colors duration-300", getTextColor())}>
            <LanguageSwitcher 
              className={cn(
                "border",
                getTextColor(),
                getBackgroundOpacity() > 0.5 ? "border-white/20" : "border-black/20"
              )}
            />
          </div>
        </div>

        <button 
          onClick={() => setIsOpen(!isOpen)} 
          className={cn(
            "md:hidden focus:outline-none transition-colors duration-300",
            getTextColor()
          )}
          aria-label="Toggle menu"
        >
          {isOpen ? (
            <X className="h-6 w-6" />
          ) : (
            <Menu className="h-6 w-6" />
          )}
        </button>
      </div>

      {/* Mobile menu */}
      {isOpen && (
        <div className="md:hidden absolute top-full left-0 w-full bg-background pb-6 px-6 shadow-md animate-fade-in">
          <div className="flex flex-col space-y-6 pt-4">
            <Link 
              to="/" 
              className="text-sm uppercase tracking-wider"
              onClick={() => setIsOpen(false)}
            >
              作品集
            </Link>
            <Link 
              to="/gallery" 
              className="text-sm uppercase tracking-wider"
              onClick={() => setIsOpen(false)}
            >
              画廊
            </Link>
            <LanguageSwitcher />
          </div>
        </div>
      )}
    </nav>
  );
};

export default NavBar;
